import { Box, List, ListItem, SvgIconProps, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ThumbDown, ThumbUp } from '@mui/icons-material';
import React from 'react';

export const PhotoDescription: React.VFC = () => {
  const { classes } = usePhotoDescriptionStyles();

  return (
    <Box className={classes.flexContainer}>
      <Box className={classes.flexItem}>
        <HeadshotsContainer
          title="A good headshot..."
          images={[
            'https://scdn.joinuplift.co/public/application-assets/good_profile_pic_1.jpg',
            'https://scdn.joinuplift.co/public/application-assets/good_profile_pic_2.jpg'
          ]}
          icon={ThumbUp}
          descriptionListItems={[
            'Is framed from the chest up',
            'Has eye contact with the camera',
            'Has good lighting on the face',
            'Showcases your personality',
          ]}
          classes={{ content: classes.successBackground, title: classes.successColor, icon: classes.successColor }}
        />
      </Box>
      <Box className={classes.flexItem}>
        <HeadshotsContainer
          title="A bad headshot..."
          images={[
            'https://scdn.joinuplift.co/public/application-assets/bad_profile_pic_1.jpg',
            'https://scdn.joinuplift.co/public/application-assets/bad_profile_pic_2.jpg'
          ]}
          icon={ThumbDown}
          descriptionListItems={[
            'Has low picture quality',
            'Is looking away from the camera',
            'Is covering the face',
            'Cannot clearly see the face',
          ]}
          classes={{ content: classes.errorBackground, title: classes.errorColor, icon: classes.errorColor }}
        />
      </Box>
    </Box>
  );
};

const usePhotoDescriptionStyles = makeStyles({ name: 'PhotoDescription' })(theme => ({
  flexContainer: {
    display: 'flex',
    gap: theme.spacing(3),
    flexWrap: 'wrap',
  },
  flexItem: {
    flex: '1 0 250px',
  },
  successColor: {
    color: theme.palette.success.main,
  },
  successBackground: {
    backgroundColor: theme.palette.success.background,
  },
  errorColor: {
    color: theme.palette.error.main,
  },
  errorBackground: {
    backgroundColor: theme.palette.error.background,
  },
}));

type HeadshotsContainerProps = {
  title: string;
  images: [string, string];
  icon: React.ElementType<SvgIconProps>;
  classes?: Partial<ReturnType<typeof useHeadshotsContainerStyles>['classes']>;
  descriptionListItems: string[];
};

const HeadshotsContainer: React.VFC<HeadshotsContainerProps> = (props) => {
  const { classes } = useHeadshotsContainerStyles(props, { props });

  return (
    <Box>
      <Typography className={classes.title}>
        {props.title}
      </Typography>
      <Box className={classes.content}>
        <Box className={classes.headshots}>
          <Box className={classes.headshotContainer}>
            <img src={props.images[0]} alt="Good headshot example 1" />
          </Box>
          <Box>
            <props.icon fontSize="small" className={classes.icon} />
          </Box>
          <Box className={classes.headshotContainer}>
            <img src={props.images[1]} alt="Good headshot example 2" />
          </Box>
        </Box>
        <List className={classes.descriptionList}>
          {props.descriptionListItems.map((item, index) => (
            <ListItem key={index} className={classes.descriptionListItem}>{item}</ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );
};

const useHeadshotsContainerStyles = makeStyles<HeadshotsContainerProps>({ name: 'HeadshotsContainer' })(theme => ({
  title: {
    display: 'block',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    textAlign: 'center',
  },
  content: {
    padding: theme.spacing(2, 2, 3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  descriptionList: {
    margin: 'unset',
    padding: theme.spacing(0, 0, 0, 1),
    listStyleType: 'disc',
  },
  descriptionListItem: {
    display: 'list-item',
    listStylePosition: 'inside',
    padding: theme.spacing(0.25, 0),
    margin: 'unset',
  },
  headshots: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: '250px',
  },
  headshotContainer: {
    display: 'flex',
    flex: '0 1 104px',
    '& img': {
      width: '100%',
      height: 'auto',
    }
  },
  icon: {},
}));
